import { useCallback, useEffect, useRef } from "react";

const SECTION_SCROLL_OFFSET = 80;

/**
 * Keep the last loaded product list visible while a new category request is in flight
 * so the page height does not collapse and throw off scroll position.
 */
export function useStableCatalogProducts(products, isLoading) {
  const cacheRef = useRef([]);

  const resetCatalogCache = useCallback(() => {
    cacheRef.current = [];
  }, []);

  useEffect(() => {
    if (products !== undefined) {
      cacheRef.current = products;
    }
  }, [products]);

  const displayProducts =
    products !== undefined ? products : isLoading ? cacheRef.current : [];

  return { displayProducts, resetCatalogCache };
}

export function useCategorySectionScroll(sectionRef) {
  const scrollToSection = useCallback(() => {
    const node = sectionRef.current;
    if (!node) return;

    const top = node.getBoundingClientRect().top + window.scrollY - SECTION_SCROLL_OFFSET;
    const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    window.scrollTo({ top: Math.min(Math.max(0, top), maxScroll), behavior: "auto" });
  }, [sectionRef]);

  const createCategoryChangeHandler = useCallback(
    (setCategorySlug, setCurrentPage) => (slug) => {
      scrollToSection();
      setCategorySlug(slug);
      if (typeof setCurrentPage === "function") {
        setCurrentPage(1);
      }
      requestAnimationFrame(scrollToSection);
    },
    [scrollToSection]
  );

  const reanchorAfterLoad = useCallback(
    (isLoading) => {
      if (isLoading || !sectionRef.current) return;

      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (window.scrollY <= maxScroll - 50) return;

      scrollToSection();
    },
    [sectionRef, scrollToSection]
  );

  return { scrollToSection, createCategoryChangeHandler, reanchorAfterLoad };
}
